/**
 * CS1 — honest bylines + review receipts (frontend typed surface).
 *
 * This is a THIN typed wrapper over the ONE canonical pure classifier
 * `shared/provenance/provenance.js` (imported via @shared, the same cross-runtime pattern as
 * `@shared/publish/transitions.js`). The judgment lives in the shared module so the browser,
 * the AI routes, and the backend MCP `.mjs` all share ONE implementation with zero drift.
 *
 * What this file adds: TS types + a couple of render ergonomics. It reads ONLY pipeline
 * provenance — NEVER `doc.author` (the author-field interlock: auto-publish stamps the owner's
 * uid as author, so rendering it would label machine content human). See the shared module.
 */
import {
  deriveProvenance as _deriveProvenance,
  renderBylineParts as _renderBylineParts,
  toPublicProvenance as _toPublicProvenance,
  mastheadIntro as _mastheadIntro,
  MASTHEAD_BACKING_LINE as _MASTHEAD_BACKING_LINE,
  LANE_LABELS as _LANE_LABELS,
  REVIEW_CLAIM_PREFIX as _REVIEW_CLAIM_PREFIX,
} from '@shared/provenance/provenance.js';

export type ContentKind = 'article' | 'breaking' | 'recap';

export type LaneKey = 'wire' | 'recaps' | 'articles' | 'briefs';
export type ProducerLane = { key: LaneKey; label: string };

/** An injected masthead identity — resolved SERVER-SIDE and passed into the pure classifier. */
export interface MastheadIdentity {
  name: string;
  handle?: string | null;
  url?: string | null;
}

/** uid → identity. The classifier never looks this up; the caller resolves and injects it. */
export type IdentityMap = Record<string, MastheadIdentity>;

export type ReviewedBy = {
  name: string;
  handle: string | null;
  url: string | null;
  at: string | null;
} | null;

export type Provenance = {
  producedBy: ProducerLane;
  reviewedBy: ReviewedBy;
  grammar: 'A' | 'B';
};

/**
 * The subset of a content doc the classifier reads. Intentionally loose so a live `Article`
 * or `BreakingNews` doc can be passed directly. The author field is deliberately ABSENT here —
 * it is never consulted.
 */
export interface ProvenanceDoc {
  transitionLog?: Array<{ from?: string; to?: string; by?: string; at?: string; kind?: string }>;
  transition?: string;
  transitionBy?: string | null;
  transitionAt?: unknown;
  category?: string;
  sourceRecapId?: string;
  // CS2 desk-brief fields (stamped by the parallel desk leg on articles-collection docs). `format`
  // drives the 'briefs' lane; `briefSource.outletName` powers the "via {Outlet}" attribution.
  format?: string;
  briefSource?: { outlet?: string; outletName?: string; url?: string };
}

/** A matching gold_records verdict, when the caller has fetched one (CS1b wires this at scale). */
export interface GoldVerdict {
  decision?: string;
  by?: string;
  ts?: string | null;
}

export type BylineParts =
  | { grammar: 'A'; prefix: string; name: string; handle: string | null; handleUrl: string | null }
  | { grammar: 'B'; text: string };

export type PublicProvenance = {
  producedBy: string;
  reviewedBy: { name: string; handle: string | null; url: string | null; at: string | null } | null;
};

/** Derive honest producer + review provenance from a doc's real publish records. Pure. */
export function deriveProvenance(
  doc: ProvenanceDoc | null | undefined,
  opts: { kind?: ContentKind; goldRecord?: GoldVerdict; identities?: IdentityMap } = {},
): Provenance {
  return _deriveProvenance(doc || {}, opts) as Provenance;
}

/** Render-ready byline parts (grammar A = structured name/handle parts; grammar B = flat string). */
export function renderBylineParts(prov: Provenance): BylineParts {
  return _renderBylineParts(prov) as BylineParts;
}

/** Machine-readable projection for AI routes / RSS / MCP (lane label + name/handle/url only; no uid). */
export function toPublicProvenance(prov: Provenance): PublicProvenance {
  return _toPublicProvenance(prov) as PublicProvenance;
}

/** RSS/AI `dc:creator` value — the producer lane label (grammar-honest, never a uid displayName). */
export function laneCreator(prov: Provenance): string {
  return prov.producedBy.label;
}

/**
 * CS2 render ergonomic — the "via {Outlet}" attribution for a desk BRIEF, or null.
 *
 * Attribute-don't-adopt: a brief SUMMARIZES a syndicated outlet's reporting, so the byline names
 * the outlet as the source ("via {Outlet}") beside the Descout · Briefs producer label. Returns
 * the outlet name ONLY when the derived lane is 'briefs' (the classifier confirmed `format:'brief'`)
 * AND a non-empty `briefSource.outletName` is present — so a brief with a missing/blank source
 * renders NOTHING (never "via undefined"). Non-brief docs always return null.
 */
export function viaOutlet(
  doc: ProvenanceDoc | null | undefined,
  prov: Provenance,
): string | null {
  if (!doc || prov.producedBy.key !== 'briefs') return null;
  const name = doc.briefSource?.outletName;
  if (typeof name !== 'string') return null;
  const trimmed = name.trim();
  return trimmed.length ? trimmed : null;
}

/** Masthead per-editor intro line (COPY-VETO §1). */
export function mastheadIntro(name: string): string {
  return _mastheadIntro(name) as string;
}

/**
 * The review CLAIM string, re-exported from the ONE module that owns it. Any render site or test
 * that needs these words imports THIS — typing the literal anywhere else breaks the grep-guard
 * (src/lib/__tests__/provenance-grep.test.ts), which is the structural proof that no review claim
 * can render without going through the classifier.
 */
export const REVIEW_CLAIM_PREFIX: string = _REVIEW_CLAIM_PREFIX as string;

export const MASTHEAD_BACKING_LINE: string = _MASTHEAD_BACKING_LINE as string;
export const LANE_LABELS: Record<LaneKey, string> = _LANE_LABELS as Record<LaneKey, string>;
